import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';

import AnalysisAiDescriptionView, {
    AI_DESCRIPTION_GAP,
    AI_DESCRIPTION_LINE_HEIGHT,
} from 'adapter/in/common/components/AnalysisAiDescriptionView';
import { BOX_PADDING_BOTTOM, BOX_PADDING_TOP, Box } from 'adapter/in/common/components/AnalysisAiDescriptionReadOnly/style';

type Props = {
    text?: string;
    isLoading?: boolean;
    maxHeight?: number;
};

const AnalysisAiDescriptionReadOnly = forwardRef<HTMLDivElement, Props>(({ text, isLoading, maxHeight }, ref) => {
    const disclaimerRef = useRef<HTMLParagraphElement | null>(null);
    const [maxLines, setMaxLines] = useState<number | undefined>(undefined);

    useLayoutEffect(() => {
        const node = disclaimerRef.current;
        if (!maxHeight || !text || !node) {
            setMaxLines(undefined);
            return undefined;
        }
        const computeMaxLines = () => {
            const available = maxHeight - BOX_PADDING_TOP - BOX_PADDING_BOTTOM - AI_DESCRIPTION_GAP - node.offsetHeight;
            setMaxLines(Math.max(1, Math.floor(available / AI_DESCRIPTION_LINE_HEIGHT)));
        };
        computeMaxLines();
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(computeMaxLines);
        observer.observe(node);
        return () => observer.disconnect();
    }, [maxHeight, text]);

    if (!text && !isLoading) {
        return null;
    }

    return (
        <Box ref={ref} data-testid='ai-description' $maxHeight={maxHeight}>
            <AnalysisAiDescriptionView
                text={text}
                isLoading={isLoading}
                descriptionTestId='ai-description-readonly'
                loadingTestId='ai-description-readonly-loading'
                descriptionMaxLines={maxLines}
                disclaimerRef={disclaimerRef}
            />
        </Box>
    );
});

AnalysisAiDescriptionReadOnly.displayName = 'AnalysisAiDescriptionReadOnly';

export default AnalysisAiDescriptionReadOnly;
